
type TimestampFormat = "MMSS" | "MM:SS" | "HH:MM:SS" | "HH:MM:SS.SSS"

export const TIME_FORMATS_REGEXP = {
    MMSS: /\b\d{4}\b/,
    MM_SS: /\b\d{2}:\d{2}\b/,
    HH_MM_SS: /\b\d{2}:\d{2}:\d{2}\b/,
    HH_MM_SS_SSS: /\b\d{2}:\d{2}:\d{2}.\d{3}\b/
}

function validateTimestamp(raw: string): { isValid: boolean; format?: TimestampFormat } {
    if (raw.length < 4) {
        return {
            isValid: false
        }
    }

    if (raw.match(TIME_FORMATS_REGEXP.HH_MM_SS_SSS)) {
        return {
            isValid: true,
            format: 'HH:MM:SS.SSS'
        }
    }
    if (raw.match(TIME_FORMATS_REGEXP.HH_MM_SS)) {
        return {
            isValid: true,
            format: 'HH:MM:SS'
        }
    }

    if (raw.match(TIME_FORMATS_REGEXP.MM_SS)) {
        return {
            isValid: true,
            format: 'MM:SS'
        }
    }

    if (raw.match(TIME_FORMATS_REGEXP.MMSS)) {
        return {
            isValid: true,
            format: 'MMSS'
        }
    }

    return {
        isValid: false
    }
}

export function timestampToSeconds(timestamp: string): number {
    const validationResult = validateTimestamp(timestamp)

    if (validationResult.isValid === false) {
        throw Error(`Invalid timestamp ${timestamp}`)
    }

    const parts = {
        minutes: 0,
        seconds: 0,
        millis: 0
    }

    if (validationResult.format === "MMSS") {
        parts.minutes = parseInt(timestamp.substr(0, 2))
        parts.seconds = parseInt(timestamp.substr(2, 2))
    }

    if (validationResult.format === "MM:SS") {
        parts.minutes = parseInt(timestamp.substr(0, 2))
        parts.seconds = parseInt(timestamp.substr(3, 2))
    }

    if (validationResult.format === "HH:MM:SS") {
        parts.minutes = parseInt(timestamp.substr(3, 2))
        parts.seconds = parseInt(timestamp.substr(6, 2))
    }

    if (validationResult.format === "HH:MM:SS.SSS") {
        parts.minutes = parseInt(timestamp.substr(3, 2))
        parts.seconds = parseInt(timestamp.substr(6, 2))
        parts.millis = parseInt(timestamp.substr(9, 3)) / 1000
    }


    if (parts.seconds > 59) {
        throw Error(`Invalid timestamp ${timestamp}`)
    }

    return (parts.minutes * 60) + (Math.round(parts.seconds + parts.millis))
}

export function secondsToTimestamp(seconds: number): string {
    if (seconds < 0) {
        throw Error(`Seconds cannot be a negative number: ${seconds}`)
    }

    return new Date(seconds * 1000).toISOString().substr(14, 5)
}

/**
 * Takes a string representing a known timestamp format and transforms
 * it to our default timestamp format if possible.
 *
 * Throws an error otherwise.
 */
export function strictParseTimestamp(timestamp: string): string {
    // Expected to throw
    const seconds = timestampToSeconds(timestamp)
    return secondsToTimestamp(seconds)
}

/**
 * Takes a string representing a known timestamp format and transforms
 * it to our default timestamp format if possible.
 *
 * If no known format is identified, the default value is provided
 * as-is.
 */
export function validTimestampOrDefault(timestamp: string, defaultTimestamp: string): string {
    try {
        return strictParseTimestamp(timestamp)
    } catch (error) {
        return defaultTimestamp
    }
}

export function ISODate(): string {
    return new Date().toISOString()
}
